import React, { useState } from "react";
import { FormWizardTemplate, AutocompleteWithLoading } from "@iventis/components";
import { SitemapVersionLevel } from "@iventis/domain-model/model/sitemapVersionLevel";
import { Body1, HorizontalGap, StyledFieldLabel } from "@iventis/styles";
import { SitemapVersion } from "@iventis/domain-model/model/sitemapVersion";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";
import { MapObjectSitemapLevelReassignmentPayload } from "@iventis/domain-model/model/mapObjectSitemapLevelReassignmentPayload";
import { useSitemapRoutingContext } from "./context/sitemap-routing-context";

export interface SitemapVersionLevelReassignment {
    removedLevel: SitemapVersionLevel;
    fallbackLevel: SitemapVersionLevel;
}

/**
 * If a level has been removed, we need to ensure any map objects that existed on that level are reassigned to a new level
 */
export const LevelReassignmentForm = ({
    availableSitemapVersions,
    existingLevelsRemoved,
    confirm,
    close,
}: {
    availableSitemapVersions: SitemapVersion[];
    existingLevelsRemoved: SitemapVersionLevel[];
    confirm: (reassignments: Omit<MapObjectSitemapLevelReassignmentPayload, "projectId">[]) => void;
    close: () => void;
}) => {
    const translate = useIventisTranslate();

    const [sitemapVersionLevelAdjustment, setSitemapVersionLevelAdjustment] = useState<{ [removedLevelId: string]: SitemapVersionLevelReassignment }>({});

    const { getSelectedSitemapId } = useSitemapRoutingContext();

    const onConfirm = () => {
        const reassignments: Omit<MapObjectSitemapLevelReassignmentPayload, "projectId">[] = Object.values(sitemapVersionLevelAdjustment).map((levelReassignment) => ({
            oldLevel: levelReassignment.removedLevel.levelIndex,
            newLevel: levelReassignment.fallbackLevel.levelIndex,
            sitemapId: getSelectedSitemapId(),
        }));
        confirm(reassignments);
        close();
    };

    const onSkip = () => {
        confirm([]);
        close();
    };

    const isValid = existingLevelsRemoved?.every((level) => sitemapVersionLevelAdjustment[level.id]?.fallbackLevel != null);

    return (
        <FormWizardTemplate
            currentStage={0}
            stages={[
                {
                    primaryButtonText: translate(Content.common.buttons.confirm),
                    primaryButtonCallback: onConfirm,
                    secondaryButtons: [
                        { buttonText: translate(Content.common.buttons.cancel), onButtonPressed: close },
                        { buttonText: translate(Content.common.buttons.skip), onButtonPressed: onSkip },
                    ],
                    isValid,
                    submitButtonDataTestId: "level-reassignment-confirm",
                },
            ]}
            title={translate(Content.sitemaps.levelsReassignmentDialog.title)}
        >
            <Body1>
                <span>{translate(Content.sitemaps.levelsReassignmentDialog.paragraph1)}</span> <br />
                <span>{translate(Content.sitemaps.levelsReassignmentDialog.paragraph2)}</span>
            </Body1>
            {existingLevelsRemoved.map((level) => (
                <React.Fragment key={level.id}>
                    <HorizontalGap height="15px" />
                    <StyledFieldLabel>{`${level.levelName} (${level.levelAbbreviation})`}</StyledFieldLabel>
                    <AutocompleteWithLoading
                        dataTestId={`level-reassignment-${level.levelName}`}
                        value={sitemapVersionLevelAdjustment[level.id]?.fallbackLevel ?? null}
                        options={availableSitemapVersions.flatMap((version) => version.sitemapVersionLevels)}
                        groupBy={(option) => availableSitemapVersions?.find((v) => v.id === option.sitemapVersionId)?.name}
                        onChange={(value) => setSitemapVersionLevelAdjustment((adjustments) => ({ ...adjustments, [level.id]: { removedLevel: level, fallbackLevel: value } }))}
                        getOptionLabel={(option: SitemapVersionLevel) => `${option.levelName} (${option.levelAbbreviation})`}
                        getOptionSelected={(option, selected) => option?.id === selected?.id}
                        loadingOptions={false}
                    />
                </React.Fragment>
            ))}
        </FormWizardTemplate>
    );
};
